/* eslint-disable react-hooks/exhaustive-deps */
/*
 * Public ban-appeal page — banned players hit `/appeal` (no sign-in
 * required) and submit a form that lands in the admin Moderation
 * Appeals queue. We deliberately keep this lightweight: no auth, no
 * captcha — the admin queue is the gatekeeper.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { PageHeader } from "@/components/Atoms";
import { ShieldAlert, Send, CheckCircle2 } from "lucide-react";

export default function Appeal() {
  const toast = useToast();
  const [form, setForm] = useState({ player_id: "", discord_id: "", contact: "", appeal_reason: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.player_id.trim() || form.appeal_reason.trim().length < 10) {
      toast.error("Player ID and a detailed reason (10+ characters) are required");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/moderation/appeals/submit", {
        player_id: form.player_id.trim(),
        appeal_reason: form.appeal_reason.trim(),
        ...(form.discord_id.trim() ? { discord_id: form.discord_id.trim() } : {}),
        ...(form.contact.trim() ? { contact: form.contact.trim() } : {}),
      });
      setSubmitted(data.appeal_id);
      toast.success(`Appeal submitted — reference #${data.appeal_id}`);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted != null) {
    return (
      <div className="max-w-xl mx-auto py-12 px-4">
        <div className="glass-card p-8 text-center fade-up" data-testid="appeal-success">
          <CheckCircle2 size={48} className="mx-auto text-emerald-400 mb-3" />
          <h1 className="text-2xl font-semibold mb-2">Appeal submitted</h1>
          <p className="text-[var(--muted)] mb-4">
            Your appeal reference is <span className="font-mono text-[var(--teal)]">#{submitted}</span>.
            An admin will review it and respond via the contact info you provided. There is no email
            notification — please check back through Discord.
          </p>
          <Link to="/" className="btn btn-ghost">← Back to site</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-xl mx-auto py-10 px-4">
      <PageHeader title="Ban Appeal" subtitle="Tell us why your ban should be reviewed." />
      <form onSubmit={submit} className="glass-card p-6 space-y-4 fade-up" data-testid="appeal-form">
        <div className="flex items-start gap-3 p-3 rounded bg-rose-500/10 border border-rose-500/30 text-sm">
          <ShieldAlert size={18} className="shrink-0 text-rose-300 mt-0.5" />
          <div>
            <strong className="text-rose-200">Submit only if you believe your ban was incorrect.</strong>
            <p className="text-[var(--muted)] mt-1">Frivolous appeals will be denied. Provide context, evidence, and your Discord ID so we can reach you.</p>
          </div>
        </div>

        <div>
          <label className="text-xs uppercase tracking-wider text-[var(--muted)] block mb-1">Your player ID (Steam/Xbox/PS)</label>
          <input
            value={form.player_id}
            onChange={(e) => setForm({ ...form, player_id: e.target.value })}
            placeholder="e.g. steam_76561198…"
            className="input"
            required
            data-testid="appeal-player-id"
          />
        </div>

        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="text-xs uppercase tracking-wider text-[var(--muted)] block mb-1">Discord ID (optional)</label>
            <input value={form.discord_id} onChange={(e) => setForm({ ...form, discord_id: e.target.value })} placeholder="e.g. 1234567890" className="input" data-testid="appeal-discord-id" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-[var(--muted)] block mb-1">Contact (optional)</label>
            <input value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} placeholder="Email or Discord username" className="input" data-testid="appeal-contact" />
          </div>
        </div>

        <div>
          <label className="text-xs uppercase tracking-wider text-[var(--muted)] block mb-1">Why should we reverse this ban?</label>
          <textarea
            value={form.appeal_reason}
            onChange={(e) => setForm({ ...form, appeal_reason: e.target.value })}
            rows={6}
            placeholder="Be specific — when did the ban happen, what were you doing, and why do you believe it was a mistake?"
            className="input"
            required
            minLength={10}
            maxLength={2000}
            data-testid="appeal-reason"
          />
          <p className="text-[10px] text-[var(--muted)] text-right mt-1">{form.appeal_reason.length} / 2000</p>
        </div>

        <button disabled={submitting} className="btn btn-teal w-full" data-testid="appeal-submit">
          <Send size={14} /> {submitting ? "Submitting…" : "Submit Appeal"}
        </button>
      </form>
    </div>
  );
}
